"use client"

import { useState, useEffect } from "react"
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import L from "leaflet"
import Button from "./Button"
import { X, MapPin, Calendar, Package } from "react-feather"

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
})

const MedicineInfoModal = ({ medicine, isOpen, onClose }) => {
  const [mapKey, setMapKey] = useState(0)
  const [details, setDetails] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (isOpen) {
      setMapKey((prev) => prev + 1)
    }
  }, [isOpen])

  useEffect(() => {
    const fetchDetails = async () => {
      if (!isOpen || !medicine?._id) return
      try {
        setLoading(true)
        setError("")
        const API_BASE = process.env.REACT_APP_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:5000`
        const res = await fetch(`${API_BASE}/api/medicines/${medicine._id}`)
        const json = await res.json()
        if (!res.ok || !json?.success) {
          throw new Error(json?.message || "Failed to load medicine details")
        }
        setDetails(json.data)
      } catch (e) {
        setError(e.message || "Error loading details")
      } finally {
        setLoading(false)
      }
    }
    fetchDetails()
  }, [isOpen, medicine?._id])

  if (!isOpen || !medicine) return null

  const pharmacy = details?.pharmacy
  const hasCoords = typeof pharmacy?.latitude === "number" && typeof pharmacy?.longitude === "number"
  const medicineLocation = {
    latitude: hasCoords ? pharmacy.latitude : null,
    longitude: hasCoords ? pharmacy.longitude : null,
    address: pharmacy?.address || "Location not available",
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-600" />
            Medicine Details
          </h2>
          <Button variant="secondary" size="sm" onClick={onClose} className="flex items-center gap-1">
            <X className="w-4 h-4" />
            Close
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Medicine Information */}
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-4 flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Medicine Information
                </h3>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medicine Name</label>
                    <p className="text-lg font-semibold text-gray-900">{medicine.name}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content/Dosage</label>
                    <p className="text-gray-900">{medicine.content}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 capitalize">
                      {medicine.type}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Measurement Unit</label>
                    <p className="text-gray-900">{medicine.measurementUnit}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                    <p className="text-gray-900">{medicine.country}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Expiration Date
                    </label>
                    <p className="text-gray-900 font-medium">
                      {new Date(medicine.expireDate).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Location Information
                </h3>

                {/* Address */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  {loading ? (
                    <p className="text-gray-500">Loading...</p>
                  ) : error ? (
                    <p className="text-red-600">{error}</p>
                  ) : (
                    <p className="text-gray-900">{medicineLocation.address}</p>
                  )}
                </div>

                {/* React Leaflet Map */}
                <div className="rounded-lg overflow-hidden h-64 border border-gray-300">
                  {medicineLocation.latitude != null && medicineLocation.longitude != null ? (
                    <MapContainer
                      key={mapKey}
                      center={[medicineLocation.latitude, medicineLocation.longitude]}
                      zoom={16}
                      style={{ height: "100%", width: "100%" }}
                      scrollWheelZoom={false}
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      <Marker position={[medicineLocation.latitude, medicineLocation.longitude]}>
                        <Popup>
                          <div className="text-center">
                            <strong>{medicine.name}</strong>
                            <br />
                            {medicineLocation.address}
                          </div>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-sm text-gray-600">
                      Location coordinates not available
                    </div>
                  )}
                </div>

                {/* Coordinates */}
                <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Latitude:</span>
                    <span className="ml-1 text-gray-900">{medicineLocation.latitude ?? "N/A"}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Longitude:</span>
                    <span className="ml-1 text-gray-900">{medicineLocation.longitude ?? "N/A"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex justify-end gap-3 pt-4 border-t">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={() => {
                if (medicineLocation.latitude != null && medicineLocation.longitude != null) {
                  window.open(
                    `https://maps.google.com/?q=${medicineLocation.latitude},${medicineLocation.longitude}`,
                    "_blank",
                  )
                }
              }}
            >
              View on Google Maps
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MedicineInfoModal
